import { useEffect, useCallback, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { PHASES, buildProfile } from './data/constants';
import LandingPage from './components/LandingPage';
import MarketingSite from './components/Marketing';
import Auth from './components/Auth';
import Consent from './components/Consent';
import Demographics from './components/Demographics';
import BaselineSurvey from './components/BaselineSurvey';
import Coaching from './components/Coaching';
import PostTest from './components/PostTest';
import Complete from './components/Complete';
import AdminDashboard from './components/AdminDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'nudge-user-v1';

const PHASE_NAMES = {
  [PHASES.MARKETING]:    'marketing',
  [PHASES.AUTH]:         'auth',
  [PHASES.CONSENT]:      'consent',
  [PHASES.DEMOGRAPHICS]: 'demographics',
  [PHASES.BASELINE]:     'baseline',
  [PHASES.COACHING]:     'coaching',
  [PHASES.POSTTEST]:     'posttest',
  [PHASES.COMPLETE]:     'complete',
};

// Sync user data to backend (fire-and-forget)
function syncToBackend(user, extra = {}) {
  if (!user?.studyId) return;
  axios.post(`${BACKEND_URL}/api/participant`, {
    study_id:      user.studyId,
    email:         user.email || null,
    phase:         PHASE_NAMES[user.currentPhase] || 'unknown',
    demographics:  user.demographics  || null,
    baseline:      user.baseline      || null,
    profile:       user.profile       || null,
    posttest:      user.posttest      || null,
    coaching_week: user.coachingWeek  ?? null,
    coaching_day:  user.coachingDay   ?? null,
    registered_at: user.registeredAt  || null,
    ...extra,
  }).catch(() => {}); // silent — localStorage is the source of truth
}

// Stage 1 App (existing functionality)
function Stage1App() {
  const [phase, setPhase]   = useState(PHASES.MARKETING);
  const [user,  setUser]    = useState(null);
  const navigate = useNavigate();

  // Load user from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setPhase(PHASES.MARKETING);
      return;
    }
    try {
      const userData = JSON.parse(stored);
      setUser(userData);
      setPhase(userData.currentPhase || PHASES.MARKETING);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setPhase(PHASES.MARKETING);
    }
  }, []);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    }
  }, [user]);

  const saveUser = useCallback((updates) => {
    setUser(prev => {
      const next = { ...prev, ...updates };
      syncToBackend(next);
      return next;
    });
  }, []);

  const goToPhase = useCallback((nextPhase) => {
    setPhase(nextPhase);
    saveUser({ currentPhase: nextPhase });
  }, [saveUser]);

  const handleAuth = useCallback((email, isNewUser) => {
    if (isNewUser) {
      const newUser = {
        studyId:       `P-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        email,
        currentPhase:  PHASES.CONSENT,
        registeredAt:  new Date().toISOString(),
      };
      setUser(newUser);
      syncToBackend(newUser);
      setPhase(PHASES.CONSENT);
    } else {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const userData = JSON.parse(stored);
        if (userData.email === email) {
          setUser(userData);
          setPhase(userData.currentPhase || PHASES.COACHING);
        }
      }
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setPhase(PHASES.MARKETING);
  }, []);

  const handleDemographics = useCallback((data) => {
    saveUser({ demographics: data });
    goToPhase(PHASES.BASELINE);
  }, [saveUser, goToPhase]);

  const handleBaselineComplete = useCallback((answers) => {
    const profile = buildProfile(answers);
    saveUser({
      baseline:      answers,
      profile,
      coachingWeek:  0,
      coachingDay:   0,
    });
    goToPhase(PHASES.COACHING);
  }, [saveUser, goToPhase]);

  const handleCoachingProgress = useCallback((week, day) => {
    if (week >= 4) {
      goToPhase(PHASES.POSTTEST);
    } else {
      saveUser({ coachingWeek: week, coachingDay: day });
    }
  }, [saveUser, goToPhase]);

  const handlePostTestComplete = useCallback((answers) => {
    saveUser({ posttest: answers });
    goToPhase(PHASES.COMPLETE);
  }, [saveUser, goToPhase]);

  // Render based on phase
  if (phase === PHASES.MARKETING) {
    return <MarketingSite onStart={() => goToPhase(PHASES.AUTH)} />;
  }
  if (phase === PHASES.AUTH) {
    return <Auth onSubmit={handleAuth} />;
  }
  if (phase === PHASES.CONSENT) {
    return <Consent onAccept={() => goToPhase(PHASES.DEMOGRAPHICS)} onDecline={handleLogout} />;
  }
  if (phase === PHASES.DEMOGRAPHICS) {
    return <Demographics onSubmit={handleDemographics} onLogout={handleLogout} />;
  }
  if (phase === PHASES.BASELINE) {
    return <BaselineSurvey onComplete={handleBaselineComplete} onLogout={handleLogout} />;
  }
  if (phase === PHASES.COACHING) {
    return <Coaching user={user} onProgress={handleCoachingProgress} onComplete={() => goToPhase(PHASES.POSTTEST)} onLogout={handleLogout} />;
  }
  if (phase === PHASES.POSTTEST) {
    return <PostTest onComplete={handlePostTestComplete} onLogout={handleLogout} />;
  }
  if (phase === PHASES.COMPLETE) {
    return <Complete user={user} onLogout={handleLogout} />;
  }

  return null;
}

// Main App Router
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainLanding />} />
        <Route path="/stage1/*" element={<Stage1App />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

function MainLanding() {
  const navigate = useNavigate();
  
  return (
    <LandingPage 
      onNavigate={(stage) => {
        if (stage === 'stage1') {
          navigate('/stage1');
        }
      }} 
    />
  );
}

export default App;
